import { useMemo, useState, type FormEvent } from 'react'
import { Sparkles } from 'lucide-react'
import { giftablePlans, PROMO_NOTE_MAX, type PromoCode, type PromoCodeKind } from '@manaro/contracts'
import { planLabel } from '@/features/salons/labels.ts'
import { ApiError } from '@/shared/api/client.ts'
import { plural } from '@/shared/lib/format.ts'
import { Button } from '@/shared/ui/button.tsx'
import { Chip } from '@/shared/ui/chip.tsx'
import { Field } from '@/shared/ui/field.tsx'
import { Alert } from '@/shared/ui/states.tsx'
import {
  EMPTY_PROMO_FORM,
  generateCode,
  MONTH_PRESETS,
  parseCreateForm,
  PAYMENT_PRESETS,
  PERCENT_PRESETS,
  toDateInput,
  typedCode,
  type PromoForm,
} from '../form.ts'
import { monthsLabel, promoTerms } from '../labels.ts'
import { useCreatePromoCode } from '../use-promo-codes.ts'
import { DialogActions, FieldHint, PromoDialog } from './promo-dialog.tsx'

const KIND_OPTIONS: Array<{ value: PromoCodeKind; label: string }> = [
  { value: 'discount', label: 'Скидка на оплату' },
  { value: 'free_months', label: 'Бесплатные месяцы' },
]

const paymentsChip = (count: number): string => `${count} ${plural(count, ['оплата', 'оплаты', 'оплат'])}`

const numeric = (value: string): string => value.replace(/\D/g, '')

function Legend({ children }: { children: string }) {
  return <legend className="mb-1.5 text-[13px] font-medium text-ink-soft">{children}</legend>
}

/** The salon's side of the deal in plain words, so the owner sees what they are about to give away. */
function preview(form: PromoForm): string {
  const terms = promoTerms({
    kind: form.kind,
    discountPercent: Number(form.discountPercent) || 0,
    discountPayments: form.discountPayments,
    plan: form.plan,
    months: form.months,
  })
  return `Салон получит: ${terms}.`
}

interface CreatePromoDialogProps {
  onClose: () => void
  onDone: (promo: PromoCode) => void
}

export function CreatePromoDialog({ onClose, onDone }: CreatePromoDialogProps) {
  const today = useMemo(() => toDateInput(new Date()), [])
  const [form, setForm] = useState<PromoForm>(EMPTY_PROMO_FORM)
  const [validationError, setValidationError] = useState<string | null>(null)
  const create = useCreatePromoCode()

  const update = (patch: Partial<PromoForm>) => {
    setForm((current) => ({ ...current, ...patch }))
    setValidationError(null)
  }

  const dirty = form.code !== '' || form.note.trim() !== '' || form.maxRedemptions !== '' || form.expiresAt !== ''

  const error =
    validationError ??
    (create.error ? (create.error instanceof ApiError ? create.error.message : 'Не получилось — попробуйте ещё раз.') : null)

  return (
    <PromoDialog
      titleId="create-promo-title"
      title="Новый промокод"
      subtitle="Салон вводит его в CRM при регистрации, при создании салона или на странице тарифов."
      busy={create.isPending}
      dirty={dirty}
      onClose={onClose}
    >
      {(close) => {
        function submit(event: FormEvent) {
          event.preventDefault()
          if (create.isPending) return

          const parsed = parseCreateForm(form, today)
          if (!parsed.ok) {
            setValidationError(parsed.error)
            return
          }
          create.mutate(parsed.request, {
            onSuccess: (promo) => {
              onDone(promo)
              close()
            },
          })
        }

        return (
          <form onSubmit={submit} noValidate className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Field
                label="Код"
                value={form.code}
                onChange={(event) => update({ code: typedCode(event.target.value) })}
                placeholder="Например, SPRING20"
                autoCapitalize="characters"
                autoComplete="off"
                spellCheck={false}
                className="font-mono tracking-[0.04em] max-md:text-[16px]"
              />
              {/* Outside the <label>: a button inside it would take the label's clicks and its name. */}
              <div className="flex items-center justify-between gap-3">
                <FieldHint>Латиница и цифры, от 5 символов.</FieldHint>
                <button
                  type="button"
                  onClick={() => update({ code: generateCode() })}
                  className="inline-flex shrink-0 items-center gap-1 text-[12.5px] font-medium text-brand hover:text-brand-dark max-md:-my-3 max-md:py-3"
                >
                  <Sparkles className="size-3.5" aria-hidden />
                  Придумать код
                </button>
              </div>
            </div>

            <fieldset className="flex flex-col">
              <Legend>Что даёт код</Legend>
              <div className="flex flex-wrap gap-1.5 max-sm:[&>button]:min-h-11">
                {KIND_OPTIONS.map((option) => (
                  <Chip key={option.value} label={option.label} active={form.kind === option.value} onClick={() => update({ kind: option.value })} />
                ))}
              </div>
            </fieldset>

            {form.kind === 'discount' ? (
              <>
                <div className="flex flex-col gap-2">
                  <Field
                    label="Скидка, %"
                    inputMode="numeric"
                    value={form.discountPercent}
                    onChange={(event) => update({ discountPercent: numeric(event.target.value).slice(0, 2) })}
                    className="max-md:text-[16px]"
                  />
                  <div role="group" aria-label="Быстрый выбор скидки" className="flex flex-wrap gap-1.5 max-sm:[&>button]:min-h-11">
                    {PERCENT_PRESETS.map((percent) => (
                      <Chip
                        key={percent}
                        tone="tint"
                        label={`${percent}%`}
                        active={form.discountPercent === String(percent)}
                        onClick={() => update({ discountPercent: String(percent) })}
                      />
                    ))}
                  </div>
                </div>
                <fieldset className="flex flex-col">
                  <Legend>На сколько оплат тарифа</Legend>
                  <div className="flex flex-wrap gap-1.5 max-sm:[&>button]:min-h-11">
                    {PAYMENT_PRESETS.map((count) => (
                      <Chip
                        key={count}
                        tone="tint"
                        label={paymentsChip(count)}
                        active={form.discountPayments === count}
                        onClick={() => update({ discountPayments: count })}
                      />
                    ))}
                  </div>
                  <div className="mt-1.5">
                    <FieldHint>
                      Скидка действует и на годовую оплату: одна «оплата» — это месяц или сразу целый год. Большой процент
                      на годовую — это почти год бесплатно. SMS и места мастеров — по полной цене.
                    </FieldHint>
                  </div>
                </fieldset>
              </>
            ) : (
              <>
                <fieldset className="flex flex-col">
                  <Legend>Тариф</Legend>
                  <div className="flex flex-wrap gap-1.5 max-sm:[&>button]:min-h-11">
                    {giftablePlans.map((plan) => (
                      <Chip key={plan} label={planLabel(plan)} active={form.plan === plan} onClick={() => update({ plan })} />
                    ))}
                  </div>
                </fieldset>
                <fieldset className="flex flex-col">
                  <Legend>Сколько месяцев</Legend>
                  <div className="flex flex-wrap gap-1.5 max-sm:[&>button]:min-h-11">
                    {MONTH_PRESETS.map((months) => (
                      <Chip
                        key={months}
                        tone="tint"
                        label={monthsLabel(months)}
                        active={form.months === months}
                        onClick={() => update({ months })}
                      />
                    ))}
                  </div>
                  <div className="mt-1.5">
                    <FieldHint>
                      Тариф дарится сразу, как салон введёт код. Оплаченный период подарок не заменяет — тогда код не
                      применится.
                    </FieldHint>
                  </div>
                </fieldset>
              </>
            )}

            <div className="grid gap-3 sm:grid-cols-2">
              <Field
                label="Сколько раз можно ввести"
                inputMode="numeric"
                value={form.maxRedemptions}
                onChange={(event) => update({ maxRedemptions: numeric(event.target.value).slice(0, 6) })}
                placeholder="Без лимита"
                className="max-md:text-[16px]"
              />
              <Field
                label="Последний день"
                type="date"
                min={today}
                value={form.expiresAt}
                onChange={(event) => update({ expiresAt: event.target.value })}
                className="max-md:text-[16px]"
              />
            </div>
            <FieldHint>Пустые поля — без лимита и бессрочно. Каждый владелец салона может воспользоваться кодом один раз.</FieldHint>

            <label className="flex flex-col gap-1.5">
              <span className="flex items-baseline justify-between gap-2.5">
                <span className="text-[13px] font-medium text-ink-soft">Заметка для команды</span>
                <span className="tabular text-[12px] text-ink-faint">
                  {form.note.length}/{PROMO_NOTE_MAX}
                </span>
              </span>
              <textarea
                value={form.note}
                onChange={(event) => update({ note: event.target.value })}
                rows={2}
                maxLength={PROMO_NOTE_MAX}
                placeholder="Например: рассылка ноября, блогер Олена."
                className="resize-y rounded-control border border-line-strong bg-surface-soft px-3.5 py-2.5 text-[16px] text-ink outline-none placeholder:text-ink-muted focus:border-brand focus:shadow-[0_0_0_3px_var(--color-brand-ring)] md:text-[14.5px]"
              />
            </label>

            <p className="rounded-control bg-brand-tint px-3.5 py-2.5 text-[13.5px] font-medium text-brand-ink">{preview(form)}</p>

            {error && <Alert>{error}</Alert>}

            <DialogActions>
              <Button variant="secondary" size="sm" disabled={create.isPending} onClick={close}>
                Отмена
              </Button>
              <Button type="submit" size="sm" loading={create.isPending}>
                Создать код
              </Button>
            </DialogActions>
          </form>
        )
      }}
    </PromoDialog>
  )
}
